import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, RefreshCw, Activity, Server, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminClientErrors() {
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sinceHours, setSinceHours] = useState('24');
  const [expanded, setExpanded] = useState({});

  // Use the Super Admin token (this page lives under /super-admin/*). The
  // tenant `token` key in localStorage is empty here, so the original wiring
  // always produced a 401 + toast even though the API itself works fine.
  const headers = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` }), []);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const params = sinceHours && sinceHours !== 'all' ? `?since_hours=${sinceHours}&limit=300` : '?limit=300';
      const [errRes, sumRes] = await Promise.all([
        axios.get(`${API}/platform/client-errors${params}`, { headers: headers() }),
        axios.get(`${API}/platform/client-errors/summary`, { headers: headers() }),
      ]);
      setErrors(errRes.data.errors || []);
      setSummary(sumRes.data);
    } catch (e) {
      console.error('Failed to load client errors:', e);
      toast.error('Failed to load client errors');
    } finally {
      setLoading(false);
    }
  }, [sinceHours, headers]);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  const sourceColor = (src) => ({
    'window.onerror': 'bg-rose-100 text-rose-800',
    'unhandledrejection': 'bg-amber-100 text-amber-800',
    'axios': 'bg-blue-100 text-blue-800',
    'manual': 'bg-slate-100 text-slate-700',
  }[src] || 'bg-slate-100 text-slate-700');

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="super-admin-client-errors">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-blue-900 flex items-center gap-2"><AlertTriangle size={28} className="text-rose-600" /> Live Client Errors</h1>
          <p className="text-slate-600 text-sm mt-1">Unhandled JS errors, API 5xx, and promise rejections captured from tenant browsers in real-time.</p>
        </div>
        <Button data-testid="refresh-client-errors-btn" variant="outline" onClick={fetchErrors} disabled={loading} className="border-blue-200 text-blue-700 hover:bg-blue-50">
          <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-rose-700 flex items-center gap-2"><Activity size={14} /> Last hour</CardTitle></CardHeader><CardContent className="pt-0"><div className="text-3xl font-extrabold text-rose-700" data-testid="errors-last-hour">{summary.last_hour}</div></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-amber-700 flex items-center gap-2"><Activity size={14} /> Last 24 hours</CardTitle></CardHeader><CardContent className="pt-0"><div className="text-3xl font-extrabold text-amber-700" data-testid="errors-last-day">{summary.last_day}</div></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-700 flex items-center gap-2"><Server size={14} /> All time</CardTitle></CardHeader><CardContent className="pt-0"><div className="text-3xl font-extrabold text-slate-700" data-testid="errors-total">{summary.total}</div></CardContent></Card>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-slate-600 font-medium">Show errors from:</span>
        <Select value={sinceHours} onValueChange={setSinceHours}>
          <SelectTrigger className="w-44" data-testid="filter-since-hours"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 1 hour</SelectItem>
            <SelectItem value="24">Last 24 hours</SelectItem>
            <SelectItem value="168">Last 7 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500" data-testid="errors-count-label">{errors.length} error{errors.length === 1 ? '' : 's'}</span>
      </div>

      {loading && errors.length === 0 ? (
        <div className="text-center py-12 text-slate-500">Loading client errors…</div>
      ) : errors.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500" data-testid="no-errors-message">
          <AlertTriangle size={32} className="mx-auto mb-3 text-emerald-500" />
          <p className="font-medium">No errors in this window.</p>
          <p className="text-xs mt-1">Either your app is healthy or no users have hit a crash recently.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {errors.map((e) => (
            <Card key={e.id} className="border-slate-200" data-testid={`error-row-${e.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setExpanded(p => ({ ...p, [e.id]: !p[e.id] }))}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge className={sourceColor(e.source)}>{e.source}</Badge>
                      {e.tenant_name && <Badge variant="outline" className="border-blue-300 text-blue-700">{e.tenant_name}</Badge>}
                      {e.role && <Badge variant="outline" className="text-xs">{e.role}</Badge>}
                      <span className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm font-mono text-slate-800 break-all line-clamp-2">{e.message}</p>
                    {e.url && <p className="text-xs text-slate-500 mt-1 truncate">at {e.url}</p>}
                  </div>
                  {expanded[e.id] ? <ChevronDown size={16} className="text-slate-400 flex-shrink-0 mt-1" /> : <ChevronRight size={16} className="text-slate-400 flex-shrink-0 mt-1" />}
                </div>
                {expanded[e.id] && (
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-2 text-xs">
                    {e.file && <div><span className="font-semibold text-slate-600">Location:</span> <code className="bg-slate-100 px-1 rounded">{e.file}:{e.line}:{e.column}</code></div>}
                    {e.user_agent && <div><span className="font-semibold text-slate-600">UA:</span> <span className="text-slate-500">{e.user_agent}</span></div>}
                    {e.stack && (
                      <div>
                        <p className="font-semibold text-slate-600 mb-1">Stack:</p>
                        <pre className="bg-slate-900 text-slate-100 p-2 rounded text-xs overflow-x-auto max-h-64">{e.stack}</pre>
                      </div>
                    )}
                    {e.extra && Object.keys(e.extra).length > 0 && (
                      <div>
                        <p className="font-semibold text-slate-600 mb-1">Extra:</p>
                        <pre className="bg-slate-100 text-slate-800 p-2 rounded text-xs overflow-x-auto">{JSON.stringify(e.extra, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
